/**
 * My Safe Spaces Website - App.js
 * ================================
 * Main router configuration
 */

import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import IntellicareWidget from './components/IntellicareWidget';
import ErrorBoundary from './components/ErrorBoundary';
import { ContactModalProvider, useContactModal } from './context/ContactModalContext';

// Eagerly loaded pages (above the fold)
import Home from './pages/Home';

// Lazy loaded pages
const Schools = lazy(() => import('./pages/Schools'));
const HigherEd = lazy(() => import('./pages/HigherEd'));
const Personal = lazy(() => import('./pages/Personal'));
const Workplace = lazy(() => import('./pages/Workplace'));
const Professional = lazy(() => import('./pages/Professional'));
const MobileApp = lazy(() => import('./pages/MobileApp'));
const ThriveAcademy = lazy(() => import('./pages/ThriveAcademy'));
const IntellicareAI = lazy(() => import('./pages/IntellicareAI'));
const MOSAICAssessments = lazy(() => import('./pages/MOSAICAssessments'));
const MSSLabs = lazy(() => import('./pages/MSSLabs'));
const Alumni = lazy(() => import('./pages/Alumni'));
const JobBoard = lazy(() => import('./pages/JobBoard'));
const ElevateOutreach = lazy(() => import('./pages/ElevateOutreach'));
const About = lazy(() => import('./pages/About'));
const Leadership = lazy(() => import('./pages/Leadership'));
const StrategicAdvisors = lazy(() => import('./pages/StrategicAdvisors'));
const CertificationCouncil = lazy(() => import('./pages/CertificationCouncil'));
const Partners = lazy(() => import('./pages/Partners'));
const Careers = lazy(() => import('./pages/Careers'));
const JobDetail = lazy(() => import('./pages/Careers').then(m => ({ default: m.JobDetail })));
const Awareness = lazy(() => import('./pages/Awareness'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const TAISIPage = lazy(() => import('./pages/TAISI'));
const RegisterNow = lazy(() => import('./pages/RegisterNow'));
const Webinars = lazy(() => import('./pages/Webinars'));

const NotFound = lazy(() => import('./pages/NotFound'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const Accessibility = lazy(() => import('./pages/Accessibility'));

// Scroll to top on route change (page titles managed by SEO component per page)
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Loading fallback
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <div style={{ textAlign: 'center', color: '#6c757d' }}>Loading...</div>
  </div>
);

// Inner layout that can use the contact modal context
const MainLayoutInner = ({ children }) => {
  const { openContactModal } = useContactModal();

  return (
    <div className="app">
      <ScrollToTop />
      <Header onContactClick={() => openContactModal()} />
      <main>{children}</main>
      <Footer />
      <IntellicareWidget />
    </div>
  );
};

// Layout component for pages with header/footer
const MainLayout = ({ children }) => (
  <ContactModalProvider>
    <MainLayoutInner>{children}</MainLayoutInner>
  </ContactModalProvider>
);

// Standalone layout for special pages (no header/footer)
const StandaloneLayout = ({ children }) => (
  <div className="app standalone">
    <ScrollToTop />
    <main>{children}</main>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Standalone TAISI Landing page (no header/footer) */}
          <Route path="/taisi" element={<StandaloneLayout><TAISIPage /></StandaloneLayout>} />

          {/* All other routes with main layout */}
          <Route path="/*" element={
            <MainLayout>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/schools" element={<Schools />} />
                  <Route path="/higher-education" element={<HigherEd />} />
                  <Route path="/personal" element={<Personal />} />
                  <Route path="/mobile-app" element={<MobileApp />} />
                  <Route path="/workplace" element={<Workplace />} />
                  <Route path="/professional" element={<Professional />} />
                  <Route path="/thrive-academy" element={<ThriveAcademy />} />
                  <Route path="/intellicare-ai" element={<IntellicareAI />} />
                  <Route path="/mosaic" element={<MOSAICAssessments />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/about/leadership" element={<Leadership />} />
                  <Route path="/about/strategic-advisors" element={<StrategicAdvisors />} />
                  <Route path="/about/certification-council" element={<CertificationCouncil />} />
                  <Route path="/about/partners" element={<Partners />} />
                  {/* Careers/Jobs routes */}
                  <Route path="/careers" element={<Careers />} />
                  <Route path="/careers/:jobId" element={<JobDetail />} />

                  {/* Retired: Partner Network → redirect to Partners */}
                  <Route path="/partner-network" element={<Navigate to="/about/partners" replace />} />

                  {/* Resources routes */}
                  <Route path="/resources/blogs" element={<Blog />} />
                  <Route path="/resources/awareness" element={<Awareness />} />
                  <Route path="/resources/labs" element={<MSSLabs />} />
                  <Route path="/resources/alumni" element={<Alumni />} />
                  <Route path="/resources/jobs" element={<JobBoard />} />
                  <Route path="/resources/elevate-outreach" element={<ElevateOutreach />} />

                  {/* Blog routes */}
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />

                  {/* Webinars */}
                  <Route path="/webinars" element={<Webinars />} />

                  {/* Registration */}
                  <Route path="/register-now" element={<RegisterNow />} />

                  {/* Legal pages */}
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/accessibility" element={<Accessibility />} />

                  {/* SWISS redirects */}
                  <Route path="/swiss" element={<Navigate to="/schools" replace />} />

                  {/* 404 catch-all */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </MainLayout>
          } />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
